/**
 * Builds the `filters` block of a worklist fetch payload.
 *
 * Every worklist fetch — initial load, filter/search refetch, pagination and the
 * archive toggle — routes through here so the call sites cannot drift.
 */

import { FilterValues } from "../../../data-model/filterValues";
import { processFilterData } from "./processFilterData";
import {
  expandResponseStatusValues,
  isAnyOf,
  RESPONSE_STATUS_COLUMN,
  WorklistFilterCondition,
  WorklistFilterGroup,
} from "./worklistFilterNodes";

export type {
  WorklistFilterCondition,
  WorklistFilterGroup,
} from "./worklistFilterNodes";

/** Which worklist the payload is being built for. */
export enum CallingWorklistView {
  /** Non-ePA worklist: outreach orders that are not awaiting a payer response. */
  NON_EPA = "non_epa",
  /** Pending worklist: orders flagged with `epa_pending_required`. */
  PENDING = "pending",
}

/** Values of the flag column each list fetches. */
const REQUIRED_VALUES = {
  active: ["yes"],
  archive: ["archive"],
};

/** Column flagging an order onto the non-ePA worklist. */
const NON_EPA_REQUIRED_COLUMN = "non_epa_required";

/** Column flagging an order onto the Pending worklist. */
const EPA_PENDING_REQUIRED_COLUMN = "epa_pending_required";

export interface BuildWorklistFiltersInput {
  /** Worklist the payload is for; defaults to the non-ePA list. */
  view?: CallingWorklistView;
  /** When true, fetch archived orders instead of the active list. */
  isArchiveView?: boolean;
  /** Filters the user applied through the table's filter panel. */
  validFilters?: FilterValues[];
  /** Comma-separated MRN search box contents. */
  searchTerm?: string;
}

/** Filter-panel selections, keyed by column. */
type UserFilters = Record<string, string[] | null>;

/**
 * Splits the search box contents into a list of MRNs.
 *
 * @input searchTerm - Raw search box value
 * @return Trimmed, non-empty MRNs
 */
const parseMrnSearch = (searchTerm: string): string[] =>
  searchTerm
    .split(",")
    .map((mrn) => mrn.trim())
    .filter((mrn) => mrn.length > 0);

/**
 * Builds the flag-column leaf that decides which worklist the rows come from.
 *
 * @input view - Worklist being fetched
 * @input isArchiveView - True when the archive toggle is on
 * @return Condition on the view's flag column
 */
const buildViewFilter = (
  view: CallingWorklistView,
  isArchiveView: boolean,
): WorklistFilterCondition =>
  isAnyOf(
    view === CallingWorklistView.PENDING
      ? EPA_PENDING_REQUIRED_COLUMN
      : NON_EPA_REQUIRED_COLUMN,
    isArchiveView ? REQUIRED_VALUES.archive : REQUIRED_VALUES.active,
  );

/**
 * Converts a column's selected values into the list sent in an `is_any_of` leaf.
 *
 * Response status expands known spelling variants so title-case RPA values and
 * lowercase Call Summary values both match.
 *
 * @input column - Filter column name
 * @input values - Values selected in the filter panel
 * @return Values to put on the condition node
 */
const valuesForColumn = (column: string, values: string[]): string[] =>
  column === RESPONSE_STATUS_COLUMN
    ? expandResponseStatusValues(values)
    : values;

/**
 * Converts the filter panel's selections into leaf conditions, skipping the
 * view flag columns. Response status is included whenever the user picks it —
 * including Pending — on both worklists.
 *
 * @input userFilters - Filter-panel selections
 * @return Condition nodes, one per filtered column
 */
const buildUserFilterConditions = (
  userFilters: UserFilters,
): WorklistFilterCondition[] => {
  const viewOwnedColumns = [
    NON_EPA_REQUIRED_COLUMN,
    EPA_PENDING_REQUIRED_COLUMN,
  ];

  return Object.entries(userFilters)
    .filter(
      ([column, values]) =>
        !viewOwnedColumns.includes(column) && (values?.length ?? 0) > 0,
    )
    .map(([column, values]) =>
      isAnyOf(column, valuesForColumn(column, values as string[])),
    );
};

/**
 * Builds the filters for a worklist fetch.
 *
 * Each list is defined by its own flag column: the non-ePA list by
 * `non_epa_required` alone (plus optional user-selected response statuses), the
 * Pending list by `epa_pending_required` alone. The archive view ignores the
 * user's filter panel, matching the archive icon's behaviour.
 *
 * @input input - View, archive flag, applied filters and search text
 * @return Predicate tree for the fetch payload
 */
export const buildWorklistFilters = (
  input: BuildWorklistFiltersInput,
): WorklistFilterGroup => {
  const { view = CallingWorklistView.NON_EPA, isArchiveView = false } = input;

  const userFilters: UserFilters = isArchiveView
    ? {}
    : processFilterData(input.validFilters ?? []).filters;

  const children: (WorklistFilterCondition | WorklistFilterGroup)[] = [
    buildViewFilter(view, isArchiveView),
    ...buildUserFilterConditions(userFilters),
  ];

  const mrns = parseMrnSearch(input.searchTerm ?? "");
  if (mrns.length > 0) {
    children.push(isAnyOf("patient_mrn", mrns));
  }

  return { type: "group", operator: "and", children };
};
